import React, { useEffect, useRef, useState } from "react";
import { FileText, Download, Check, X, Filter, Upload, PenLine } from "lucide-react";
import { Button } from "../components/ui/button";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import StatusBadge from "../components/StatusBadge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { useToast } from "../hooks/use-toast";
import { useAuth } from "../context/AuthContext";
import api, { errMsg } from "../api";

const DOC_TYPES = [["resume", "Resume"], ["gov-id", "Government ID"], ["w9", "W-9"], ["direct-deposit", "Direct Deposit Form"], ["ica", "Independent Contractor Agreement"]];

function Card({ children, className = "", ...rest }) {
  return <div className={`rounded-2xl border border-slate-200 bg-white p-5 shadow-sm md:p-6 ${className}`} {...rest}>{children}</div>;
}

function NoteDialog({ target, onClose, onSubmit }) {
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => setNote(""), [target]);
  const go = async () => { setBusy(true); try { await onSubmit(note); } finally { setBusy(false); } };
  return (
    <Dialog open={!!target} onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="doc-note-dialog">
        <DialogHeader><DialogTitle>{target?.status === "Rejected" ? "Reject document" : "Request a correction"}</DialogTitle></DialogHeader>
        <p className="text-sm text-slate-500">{target?.row.doc_name} for {target?.row.user_name}. The applicant will see this note.</p>
        <Textarea rows={4} value={note} onChange={(e) => setNote(e.target.value)} placeholder="Explain what needs to change…" data-testid="doc-note-input" />
        <Button onClick={go} disabled={busy || !note.trim()} data-testid="doc-note-submit" className="btn-primary-gradient border-0 text-white">{busy ? "Saving…" : "Send to applicant"}</Button>
      </DialogContent>
    </Dialog>
  );
}

function AdminUpload({ onDone, preset }) {
  const { toast } = useToast();
  const [users, setUsers] = useState([]);
  const [uid, setUid] = useState("");
  const [doc, setDoc] = useState("w9");
  const [busy, setBusy] = useState(false);
  const fileRef = useRef(null);
  useEffect(() => { api.get("/admin/users").then((r) => setUsers(r.data.filter((u) => u.role === "applicant"))).catch(() => {}); }, []);
  useEffect(() => { if (preset) { setUid(preset.user_id); setDoc(preset.doc_id); } }, [preset]);
  const upload = async () => {
    const file = fileRef.current?.files?.[0];
    if (!uid || !file) { toast({ title: "Choose an applicant and a file", variant: "destructive" }); return; }
    setBusy(true);
    try {
      const fd = new FormData(); fd.append("file", file);
      await api.post(`/admin/documents/${uid}/${doc}/upload`, fd);
      toast({ title: "Document uploaded", description: "Stored privately and marked Approved." });
      fileRef.current.value = ""; onDone();
    } catch (e) { toast({ title: "Upload failed", description: errMsg(e), variant: "destructive" }); }
    finally { setBusy(false); }
  };
  return (
    <Card data-testid="admin-doc-upload">
      <h2 className="text-base font-bold text-slate-900">Upload or replace a document for an applicant</h2>
      <div className="mt-3 grid grid-cols-1 gap-3 md:grid-cols-[1.5fr_1fr_1.5fr_auto]">
        <div className="space-y-1.5"><Label>Applicant</Label>
          <Select value={uid} onValueChange={setUid}><SelectTrigger data-testid="admin-doc-applicant"><SelectValue placeholder="Select applicant" /></SelectTrigger>
            <SelectContent>{users.map((u) => <SelectItem key={u.id} value={u.id}>{`${u.first_name} ${u.last_name}`.trim() || u.email}</SelectItem>)}</SelectContent></Select>
        </div>
        <div className="space-y-1.5"><Label>Document</Label>
          <Select value={doc} onValueChange={setDoc}><SelectTrigger data-testid="admin-doc-type"><SelectValue /></SelectTrigger>
            <SelectContent>{DOC_TYPES.map(([k, n]) => <SelectItem key={k} value={k}>{n}</SelectItem>)}</SelectContent></Select>
        </div>
        <div className="space-y-1.5"><Label>File</Label><input ref={fileRef} type="file" className="block w-full text-sm" data-testid="admin-doc-file" /></div>
        <div className="flex items-end"><Button onClick={upload} disabled={busy} className="gap-1.5" data-testid="admin-doc-upload-btn"><Upload className="h-4 w-4" />{busy ? "Uploading…" : "Upload"}</Button></div>
      </div>
    </Card>
  );
}

export default function DocumentReview() {
  const { toast } = useToast();
  const { user } = useAuth();
  const isAdmin = ["admin", "administrator"].includes(user?.role);
  const [rows, setRows] = useState([]);
  const [filter, setFilter] = useState("all");
  const [noteFor, setNoteFor] = useState(null);
  const [preset, setPreset] = useState(null);

  const load = () => {
    const params = filter === "all" ? {} : { params: { status: filter } };
    api.get("/admin/documents", params).then((r) => setRows(r.data)).catch(() => {});
  };
  useEffect(() => { load(); }, [filter]); // eslint-disable-line react-hooks/exhaustive-deps

  const review = async (row, status, notes) => {
    try {
      await api.patch(`/admin/documents/${row.user_id}/${row.doc_id}`, { status, notes });
      toast({ title: `Document marked ${status.toLowerCase()}`, description: `${row.doc_name} for ${row.user_name}.` });
      setNoteFor(null);
      load();
    } catch (err) {
      toast({ title: "Error", description: errMsg(err), variant: "destructive" });
    }
  };

  const download = async (row) => {
    try {
      const r = await api.get(`/admin/documents/${row.user_id}/${row.doc_id}/download`);
      window.open(r.data.url, "_blank", "noopener");
    } catch (err) {
      toast({ title: "Download failed", description: errMsg(err), variant: "destructive" });
    }
  };

  const pending = rows.filter((r) => r.status === "Pending").length;

  return (
    <div className="space-y-6">
      <Card>
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <h2 className="text-base font-bold text-slate-900">Document Review Center</h2>
            <p className="text-sm text-slate-500" data-testid="doc-pending-count">{pending} document(s) awaiting review.</p>
          </div>
          <div className="flex items-center gap-2">
            <Filter className="h-4 w-4 text-slate-400" />
            <Select value={filter} onValueChange={setFilter}>
              <SelectTrigger className="w-44" data-testid="doc-filter"><SelectValue /></SelectTrigger>
              <SelectContent>
                {["all", "Pending", "Needs Correction", "Approved", "Rejected"].map((s) => <SelectItem key={s} value={s}>{s === "all" ? "All" : s}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        </div>
      </Card>

      {isAdmin && <AdminUpload onDone={load} preset={preset} />}

      <Card>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-slate-100 text-left text-xs uppercase tracking-wide text-slate-400">
                <th className="py-2 pr-4">Applicant</th><th className="py-2 pr-4">Document</th><th className="py-2 pr-4">File</th><th className="py-2 pr-4">Status / notes</th><th className="py-2 text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={`${r.user_id}-${r.doc_id}`} className="border-b border-slate-50 align-top" data-testid={`doc-row-${r.user_id}-${r.doc_id}`}>
                  <td className="py-3 pr-4"><div className="font-medium text-slate-800">{r.user_name}</div><div className="text-xs text-slate-400">{r.email}</div></td>
                  <td className="py-3 pr-4 text-slate-600"><span className="inline-flex items-center gap-1.5"><FileText className="h-4 w-4 text-slate-400" />{r.doc_name}</span></td>
                  <td className="py-3 pr-4 text-slate-500">{r.filename || "—"}</td>
                  <td className="py-3 pr-4"><StatusBadge status={r.status} />{r.review_notes && <div className="mt-1 max-w-xs text-xs text-slate-500">“{r.review_notes}”</div>}</td>
                  <td className="py-3">
                    <div className="flex flex-wrap items-center justify-end gap-2">
                      <Button variant="outline" size="sm" disabled={!r.has_file} onClick={() => download(r)} data-testid={`doc-download-${r.user_id}-${r.doc_id}`}><Download className="h-3.5 w-3.5" /></Button>
                      <Button size="sm" disabled={!r.has_file || r.status === "Approved"} onClick={() => review(r, "Approved")} data-testid={`doc-approve-${r.user_id}-${r.doc_id}`} className="gap-1.5 bg-emerald-600 text-white hover:bg-emerald-700"><Check className="h-3.5 w-3.5" /> Approve</Button>
                      <Button size="sm" variant="outline" disabled={!r.has_file} onClick={() => setNoteFor({ row: r, status: "Needs Correction" })} data-testid={`doc-correction-${r.user_id}-${r.doc_id}`} className="gap-1.5"><PenLine className="h-3.5 w-3.5" /> Correction</Button>
                      <Button size="sm" variant="outline" disabled={!r.has_file || r.status === "Rejected"} onClick={() => setNoteFor({ row: r, status: "Rejected" })} data-testid={`doc-reject-${r.user_id}-${r.doc_id}`} className="gap-1.5 border-rose-200 text-rose-600 hover:bg-rose-50"><X className="h-3.5 w-3.5" /> Reject</Button>
                      {isAdmin && <Button size="sm" variant="ghost" onClick={() => { setPreset({ user_id: r.user_id, doc_id: r.doc_id }); window.scrollTo({ top: 0, behavior: "smooth" }); }} data-testid={`doc-replace-${r.user_id}-${r.doc_id}`}>Replace</Button>}
                    </div>
                  </td>
                </tr>
              ))}
              {rows.length === 0 && (<tr><td colSpan={5} className="py-8 text-center text-slate-400">No documents to review.</td></tr>)}
            </tbody>
          </table>
        </div>
      </Card>
      <NoteDialog target={noteFor} onClose={() => setNoteFor(null)} onSubmit={(note) => review(noteFor.row, noteFor.status, note)} />
    </div>
  );
}
